import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HOME } from "@/constants/testIds";
import { MapPin, Flame, Trophy, Pin, PinOff } from "lucide-react";
import { getPinned, togglePin, subscribePinned } from "@/lib/pins";
import { toast } from "sonner";

export default function CoachCard({ coach }) {
    const merit = Math.round(coach.merit_score || 0);
    const isTop = merit >= 250;
    const [pins, setPins] = useState(getPinned());
    useEffect(() => subscribePinned(setPins), []);
    const pinned = pins.includes(coach.coach_id);

    const onPin = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const res = togglePin(coach.coach_id);
        if (!res.ok && res.reason === "max") {
            toast.error("Limite atteinte", {
                description: "Compare 3 coachs maximum à la fois.",
            });
        }
    };

    return (
        <Link
            to={`/coach/${coach.coach_id}`}
            data-testid={HOME.coachCard}
            className="group flex h-full flex-col border border-white/10 bg-[#121212] transition-colors hover:border-[#FF2E2E]"
        >
            <div className="relative aspect-[4/5] w-full overflow-hidden">
                <img
                    src={coach.photo_url || "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800"}
                    alt={coach.name}
                    className="h-full w-full object-cover grayscale transition-all duration-300 group-hover:grayscale-0"
                />
                <div className="absolute inset-x-0 top-0 flex items-center justify-between p-3">
                    <span
                        className={`font-mono text-[10px] uppercase tracking-[0.3em] px-2 py-1 ${
                            coach.category === "physical"
                                ? "bg-[#FF2E2E] text-white"
                                : "border border-white/40 bg-black/60 text-white"
                        }`}
                    >
                        {coach.category === "physical" ? "Corps" : "Esprit"}
                    </span>
                    <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-widest text-white/70">
                            €{coach.hourly_rate}/h
                        </span>
                        <button
                            data-testid={`pin-${coach.coach_id}`}
                            onClick={onPin}
                            aria-label={pinned ? "Retirer de la comparaison" : "Épingler"}
                            className={`inline-flex items-center justify-center border p-1.5 transition-colors ${
                                pinned
                                    ? "border-[#FF2E2E] bg-[#FF2E2E] text-white"
                                    : "border-white/20 bg-black/60 text-white backdrop-blur-sm hover:border-[#FF2E2E] hover:text-[#FF2E2E]"
                            }`}
                        >
                            {pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                        </button>
                    </div>
                </div>

                {/* Merit badge — the headline signal */}
                <div
                    data-testid="coach-card-merit-badge"
                    className={`absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 font-display text-sm uppercase tracking-tight ${
                        isTop
                            ? "bg-[#FF2E2E] text-white"
                            : "border border-[#FF2E2E] bg-black/70 text-[#FF2E2E] backdrop-blur-sm"
                    }`}
                >
                    {isTop ? (
                        <>
                            <Trophy className="h-3.5 w-3.5" /> Top Performeur
                        </>
                    ) : (
                        <>
                            <Flame className="h-3.5 w-3.5" /> {merit} Mérite
                        </>
                    )}
                </div>
            </div>

            <div className="flex flex-1 flex-col gap-3 p-4">
                <div>
                    <h3 className="font-display text-2xl uppercase leading-none tracking-tight text-white">
                        {coach.name}
                    </h3>
                    <div className="mt-2 flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                        <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {coach.city} {coach.remote && "· À distance"}
                        </span>
                        <span>·</span>
                        <span>{coach.age} ans</span>
                    </div>
                </div>

                <div className="flex flex-wrap gap-1">
                    {(coach.specialties || []).slice(0, 3).map((s) => (
                        <span
                            key={s}
                            className="border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-neutral-300"
                        >
                            {s}
                        </span>
                    ))}
                </div>

                <div className="grid grid-cols-3 border-t border-white/10 pt-3">
                    <div>
                        <div className="font-display text-xl text-white">
                            {coach.goals_achieved}
                        </div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">
                            Objectifs
                        </div>
                    </div>
                    <div>
                        <div className="font-display text-xl text-white">
                            {coach.verified_rating?.toFixed(1) || "—"}
                        </div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">
                            Note ({coach.verified_rating_count})
                        </div>
                    </div>
                    <div>
                        <div className="font-display text-xl text-white">
                            {Math.round((coach.retention_rate || 0) * 100)}%
                        </div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">
                            Fidélité
                        </div>
                    </div>
                </div>

                <div
                    data-testid="coach-card-anti-social"
                    className="mt-auto border border-dashed border-[#FF2E2E]/30 bg-[#FF2E2E]/5 px-2 py-1.5 text-center text-[10px] uppercase tracking-[0.25em] text-[#FF2E2E]"
                >
                    ❌ Aucun lien réseau social / 0% blabla
                </div>
            </div>
        </Link>
    );
}
